import type { ErrorComponentProps } from '@tanstack/react-router'
import { useRouter } from '@tanstack/react-router'
import { TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'

/**
 * The last line of defence: a route loader or component that threw.
 *
 * Under Next this was `error.tsx`, and an uncaught throw took out the whole
 * document. Here the router catches it at the route boundary, so the shell
 * around a failed screen stays alive and interactive.
 *
 * The message is shown rather than hidden behind a generic apology. Every
 * throw this can catch is either an `ApiError` — whose message is already the
 * status and error code the API chose to expose — or one of the handful of
 * deliberate `throw new Error(...)` calls in the route loaders, which are
 * written to be read by the person hitting them. A user who can quote the
 * actual failure gets help faster than one who can only say it broke.
 */
export function RouteErrorScreen({ error }: ErrorComponentProps) {
  const router = useRouter()
  const message = error instanceof Error ? error.message : String(error)

  return (
    <div className="grid min-h-0 flex-1 place-items-center p-8">
      <div className="max-w-md text-center">
        <div className="border-card-border bg-card text-muted-foreground mx-auto grid size-11 place-items-center rounded-[var(--radius-tile)]">
          <TriangleAlert className="size-5" />
        </div>
        <h1 className="text-heading text-foreground mt-3.5">
          This screen didn&rsquo;t load
        </h1>
        <p className="text-muted-foreground mt-1.5 break-words">{message}</p>
        <Button
          className="mt-5"
          onClick={() => {
            /* Re-runs the failed loader rather than reloading the document, so
               a transient 500 or a dropped connection costs a retry instead of
               a cold start of the entire app. */
            void router.invalidate()
          }}
        >
          Try again
        </Button>
      </div>
    </div>
  )
}
